import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ProfileSite } from '@ffd/api-client';
import {
  AssistantChatWindowComponent,
  AssistantChatWindowService,
  AssistantLauncherButtonComponent,
  AssistantLauncherService,
  AssistantMessagesService,
} from '@libs/assistant';
import { AudioDeviceConnectedModalService, ShowNudgeService, ShowOnboardingService, TagEvent, TokenService } from '@libs/shared-services';
import { authActions, selectCurrentUserProfile, selectUserSiteId } from '@libs/store';
import { routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { FfdNotesAudioDeviceConnectedModalService } from '../../services/ffd-notes-audio-device-connected-modal.service';
import { FfdNotesHeaderComponent } from '../header';

@Component({
  selector: 'lib-ffd-notes-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    FfdNotesHeaderComponent,
    AssistantLauncherButtonComponent,
    AssistantChatWindowComponent,
  ],
  templateUrl: './ffd-notes-layout.component.html',
  styleUrls: ['./ffd-notes-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Everything under /ffd-notes is rendered through this layout's outlet, so overriding here covers the whole
  // product without the shared components needing to know which app they are running in.
  providers: [{ provide: AudioDeviceConnectedModalService, useClass: FfdNotesAudioDeviceConnectedModalService }],
})
export class FfdNotesLayoutComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  public readonly showOnboardingService = inject(ShowOnboardingService);
  public readonly showNudgeService = inject(ShowNudgeService);
  public readonly assistantLauncherService = inject(AssistantLauncherService);
  private readonly assistantMessagesService = inject(AssistantMessagesService);
  public readonly assistantChatWindowService = inject(AssistantChatWindowService);

  public readonly routes = routes;
  public readonly TagEvent = TagEvent;

  private readonly profile = this.store.selectSignal(selectCurrentUserProfile);
  private readonly selectedSiteId = this.store.selectSignal(selectUserSiteId);

  public readonly currentUser = computed(() => {
    const profile = this.profile();
    return profile
      ? {
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          avatarUrl: null,
          isGlobalAdmin: profile.isGlobalAdmin,
        }
      : null;
  });

  public readonly selectedSite = computed(() => {
    const profile = this.profile();
    const siteId = this.selectedSiteId();
    return profile?.sites?.find((s) => s.id === siteId) ?? null;
  });

  public readonly sitesCount = computed(() => {
    const profile = this.profile();
    return profile?.sites.filter((site) => site.id !== routes.showcaseSiteId).length ?? 0;
  });

  public readonly showNudge = computed(() => {
    return this.showNudgeService.showNudge() && !this.showOnboardingService.siteAssocMode();
  });

  public readonly isShowcase = computed(() => {
    return this.selectedSiteId() === routes.showcaseSiteId;
  });

  constructor() {
    // The /ffd-notes routes carry no :siteId, so the main layout's route-driven dispatch never runs here and
    // auth.selectedSiteId would stay null for the whole session. Every site-scoped shared feature needs it -
    // voice typing most visibly, which toasts "Site ID not available" and cannot fetch its Deepgram settings
    // or tags without it. Select the user's own practice site as soon as the profile has loaded.
    effect(() => {
      // Never override an existing selection, so this cannot fight the main layout's effect when the user
      // came from the main app.
      if (this.selectedSiteId()) {
        return;
      }

      // The showcase site is a demo site everyone has; it is never the user's own practice. With no real
      // site there is nothing correct to select, so leave the selection empty rather than guess.
      const site = this.profile()?.sites?.find((profileSite) => profileSite.id !== routes.showcaseSiteId);
      if (!site) {
        return;
      }

      this.store.dispatch(authActions.onSiteSelected({ siteId: site.id }));
    });

    effect(() => {
      const site = this.selectedSite();
      const isShowcase = this.isShowcase();
      const showOnboardingService = this.showOnboardingService;
      this.handleTrialPeriod(site, isShowcase, showOnboardingService);
    });
  }

  public ngOnInit(): void {
    this.assistantLauncherService.isFfdNotes.set(true);
    this.assistantLauncherService.init();
    this.assistantMessagesService.init();
  }

  public ngOnDestroy(): void {
    this.assistantLauncherService.isFfdNotes.set(false);
  }

  private handleTrialPeriod(site: ProfileSite | null, isShowcase: boolean, showOnboardingService: ShowOnboardingService) {
    if (isShowcase) {
      if (site?.isInsideTrialPeriod) {
        showOnboardingService.show();
      }
    } else {
      if (site?.isInsideTrialPeriod) {
        showOnboardingService.show();
      } else {
        showOnboardingService.hide();
      }
    }
  }

  public onLogout(): void {
    this.tokenService.logout();
  }

  public onSupport(): void {
    const supportCollabId = this.profile()?.supportCollaborationId;
    this.router.navigate(['/', routes.site, this.selectedSiteId() ?? routes.showcaseSiteId, routes.allCollaborations], {
      queryParams: { collaborationId: supportCollabId },
      queryParamsHandling: 'merge',
    });
  }
}
